"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/clipboard";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ArrowRight, AlertTriangle, ChevronRight, Loader2, Lock, TrendingUp, ArrowLeftRight, HelpCircle, X, Info, Mail } from "lucide-react";
import { CryptoIcon } from "@/components/CryptoIcon";
import { HONEYPOT_FIELD } from "@/lib/bot-check";
import { PendingTradeBanner } from "@/components/PendingTradeBanner";
import { useUserCountry } from "@/hooks/useUserCountry";
import { fireBeacon } from "@/lib/analytics/beacon";
import { formatLocalAmount, currencySymbolFor, validateAccountNumber } from "@/lib/locale";
import { allRegisteredCountries } from "@/lib/countries";
import { fetchWithCsrf } from "@/lib/api/fetch-csrf";
import { useDialog } from "@/hooks/useDialog";
import { ALL_CRYPTO_TYPES, type CryptoType } from "@/lib/constants/networks";
import { TRADE_LIMITS } from "@/lib/constants/limits";

type Step = "crypto" | "amount" | "payment" | "confirm" | "done";
type RateType = "fixed" | "float";
const CRYPTOS = ALL_CRYPTO_TYPES;

const NETWORK_LABELS: Record<string, string> = {
  BITCOIN: "Bitcoin",
  ETH_MAINNET: "Ethereum (high fees)",
  BSC: "BNB Chain",
  SOLANA: "Solana",
  POLYGON: "Polygon",
  TRON: "Tron (TRC-20)",
};
const NETWORK_WARNINGS: Record<string, string> = {
  ETH_MAINNET: "Gas fees $5–$30. Consider BSC or Solana for cheaper transfers.",
  BSC: "Wrong network = permanent loss of funds. Confirm you are using BSC.",
  POLYGON: "Same address format as ETH. Ensure you select Polygon network.",
  TRON: "TRC-20 addresses start with 'T'. Do not send from another chain.",
};


type PriceData = {
  marketPrice: number;
  // Default rate-type-selected ff.io variant for back-compat / fallback.
  spreadPercent: number;
  userPrice: number;
  source: string;
  // Full per-routing × per-rate-type matrix from the API.
  spreads?: {
    ffio: { fixed: number; float: number };
    own:  { fixed: number; float: number };
  };
  userPrices?: {
    ffio: { fixed: number; float: number };
    own:  { fixed: number; float: number };
  };
  ffioThresholdUsd?: number | null;
  // Per-asset minimum trade (USD). 0 / undefined = no minimum.
  minTradeUsd?: number;
  forceOwnWallet?: boolean;
};

type SavedMethod = {
  id: string;
  accountName: string;
  maskedAccountNumber: string;
  bankCode: string | null;
  bankName: string | null;
  label: string | null;
  type: string;
};

type TradeResult = {
  transactionId: string;
  depositAddress: string;
  network: string;
  networkLabel: string;
  cryptoAmount: number;
  cryptoType: string;
  localAmount: number;
  localCurrency: string;
};

const STEP_LABELS: Record<string, string> = {
  crypto: "Crypto",
  amount: "Amount",
  payment: "Payment",
  confirm: "Review",
};

const WIZARD_STEPS: Step[] = ["crypto", "amount", "payment", "confirm"];

export default function SellPage() {
  // Country selection drives currency, locale, bank list, account-number
  // validation, and the trade's countryCode. Resolves to:
  //   1. registered user's saved User.countryCode
  //   2. localStorage cookie from previous visit
  //   3. DEFAULT_COUNTRY (NG) as final fallback
  // The toggle on the amount step lets the user override.
  const { country, activeCountries, setCountry } = useUserCountry();

  // Outer-scope dialog hook — used by handleSubmit's optional email
  // prompt (anonymous-user recovery channel). Distinct instance from
  // the one in TradeCreated; each component renders its own modal.
  const { prompt: promptDialog, modal: outerDialogModal } = useDialog();

  const [step, setStep] = useState<Step>("crypto");
  const [crypto, setCrypto] = useState<CryptoType>("BTC");
  const [networks, setNetworks] = useState<string[]>([]);
  const [network, setNetwork] = useState("");
  const [amount, setAmount] = useState("");
  const [inputMode, setInputMode] = useState<"crypto" | "usd">("crypto");
  const [rateType, setRateType] = useState<RateType>("fixed");
  const [showRateHelp, setShowRateHelp] = useState(false);
  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);
  // Tracks when priceData was last refreshed so the UI can show staleness
  // and the auto-refresh effect can schedule the next pull. Independent of
  // priceData so we don't have to widen its server-controlled shape.
  const [priceFetchedAt, setPriceFetchedAt] = useState<number | null>(null);
  const [rate, setRate] = useState<{ userRate: number; currency: string } | null>(null);
  const [rateError, setRateError] = useState(false);
  const [savedMethods, setSavedMethods] = useState<SavedMethod[]>([]);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Whether the current identity (anonymous session OR registered user)
  // has accepted the CURRENT terms-of-service version. Defaults to true
  // so we don't flash the checkbox on initial render before /auth/me
  // resolves; the live value lands in setAcknowledgedCurrent below.
  // Shows the in-trade checkbox to anyone without a current acceptance:
  // anonymous traders, registered users who pre-date the gate, and
  // anyone whose acceptance went stale after a ToS effective-date bump.
  const [acknowledgedCurrent, setAcknowledgedCurrent] = useState(true);
  // Whether the user ticked the checkbox during this trade. Sent
  // alongside the trade body when the gate fires; otherwise omitted.
  const [acknowledged, setAcknowledged] = useState(false);
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [banks, setBanks] = useState<{ name: string; code: string }[]>([]);
  const [bankSearch, setBankSearch] = useState("");
  const [showBankList, setShowBankList] = useState(false);
  const [bankDropdownRect, setBankDropdownRect] = useState<DOMRect | null>(null);
  const bankTriggerRef = useRef<HTMLDivElement>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState("");
  const [resolvedAccountName, setResolvedAccountName] = useState("");
  const [saveForFuture, setSaveForFuture] = useState(true);
  // Promo code state — held alongside other trade inputs. The validation
  // result (when present + ok) is what makes the code "applied" — we
  // only send the code to the server when the preview said yes. A
  // failed preview leaves applied=null and the input still editable.
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [promoStatus, setPromoStatus] = useState<
    | { state: "idle" }
    | { state: "checking" }
    | { state: "applied"; code: string; rewardAmount: number; rewardKind: "fixed_usd" | "free_trade"; description: string }
    | { state: "rejected"; message: string }
  >({ state: "idle" });
  const [error, setError] = useState("");
  // Side-channel for action-required error states. The plain `error`
  // string drives the inline alert; this object lets specific
  // conditions render a tailored card with a CTA — e.g. anonymous
  // trade hits the $150 cap → "Create account" button. Cleared
  // alongside `setError("")`.
  const [errorAction, setErrorAction] = useState<{
    kind: "daily_limit";
    tier: "anonymous" | "verified";
    cap: number;
    traded: number;
    requested: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TradeResult | null>(null);
  // Bot-check signals. The honeypot is a hidden form field that real
  // users never see; bots auto-fill it. `pageLoadedAt` is the timestamp
  // the form mounted — submissions faster than the server's threshold
  // are rejected. Both are sent in the body alongside the trade payload.
  const [honeypot, setHoneypot] = useState("");
  const pageLoadedAtRef = useRef<number>(Date.now());

  // Spread differs per rate type, so a single price fetch is keyed by both
  // crypto and rateType. The API also returns userPriceFixed/userPriceFloat
  // in the same payload so toggling rate type re-renders the estimate from
  // the existing data instead of waiting for a network round-trip — but we
  // still re-fetch on rateType change to make sure the market price is
  // recent and `userPrice` reflects the new selection.
  const fetchPrice = useCallback(async (c: CryptoType, rt: RateType) => {
    setPriceLoading(true);
    try {
      const res = await fetch(`/api/v1/prices?crypto=${c}&rateType=${rt}`);
      if (!res.ok) return;
      const data = await res.json();
      setPriceData(data);
      setPriceFetchedAt(Date.now());
      // Funnel beacon — server dedups per (session, crypto, UTC hour).
      fireBeacon("price_quote", { cryptoType: c });
    } catch { /* keep previous */ } finally {
      setPriceLoading(false);
    }
  }, []);

  // Init: session + auth state + rate
  useEffect(() => {
    fetch("/api/v1/auth/me")
      .then((r) => r.json())
      .then((d) => {
        setIsAuthenticated(!!d.authenticated);
        // Acceptance flag: when false, the confirm-step checkbox renders
        // regardless of authentication state. /auth/me returns true if
        // the identity has accepted the current ToS effective date —
        // pre-feature users, stale versions, and unauthenticated callers
        // who haven't ticked yet all come back false.
        setAcknowledgedCurrent(d.acknowledgedCurrent !== false);
        // Admin cookie lives on its own surface (smo_admin). /auth/me exposes
        // its presence via `isAdmin` so we can block trading UI without
        // needing a separate round-trip to /api/v1/admin/auth/me.
        const admin = !!d.isAdmin;
        setIsAdmin(admin);
        if (!d.authenticated) {
          fetchWithCsrf("/api/v1/auth/session", { method: "POST" });
        } else if (!admin) {
          // Admin accounts are blocked from trading server-side; skip the
          // saved-methods fetch so we don't expose their list in a context
          // where they can't use it anyway.
          fetch("/api/v1/users/payment-methods")
            .then((r) => r.json())
            .then((methods) => {
              if (Array.isArray(methods)) {
                setSavedMethods(methods);
                const def = methods.find((m: SavedMethod) => m.type === "bank_transfer");
                if (def) setSelectedMethodId(def.id);
              }
            });
        }
      });

    // Rate + bank list fetches are triggered by the separate country effect
    // below — keeps both in lockstep with the user's current selection so
    // toggling currency refreshes everything atomically.
  }, []);

  // Fetch the local-currency rate + matching bank list whenever the user's
  // selected country changes (initial load, toggle on amount step, etc.).
  useEffect(() => {
    fetch(`/api/v1/rates?country=${country.code}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.userRate) { setRate(d); setRateError(false); }
        else setRateError(true);
      })
      .catch(() => setRateError(true));

    fetch(`/api/v1/banks?currency=${country.currency}`)
      .then((r) => r.ok ? r.json() : [])
      .then((d) => { if (Array.isArray(d)) setBanks(d); })
      .catch(() => {});
  }, [country]);

  // Auto-resolve account name when the account number length matches the
  // selected country's expected format (NG NUBAN = 10, GH = 13) AND a bank
  // is picked. validateAccountNumber pulls the expected length from the
  // country registry — adding a new country with a different length just
  // works.
  useEffect(() => {
    if (!validateAccountNumber(country.code, accountNumber) || !bankCode) {
      setResolvedAccountName("");
      setResolveError("");
      return;
    }
    setResolving(true);
    setResolveError("");
    setResolvedAccountName("");
    const controller = new AbortController();
    fetch(`/api/v1/banks/resolve?account_number=${accountNumber}&bank_code=${bankCode}`, { signal: controller.signal })
      .then((r) => r.json())
      .then((d) => {
        if (d.accountName) {
          setResolvedAccountName(d.accountName);
          setAccountName(d.accountName);
          setResolveError("");
        } else {
          setResolveError(d.error ?? "Could not verify account");
        }
      })
      .catch((e) => { if (e.name !== "AbortError") setResolveError("Could not verify account"); })
      .finally(() => setResolving(false));
    return () => controller.abort();
  }, [accountNumber, bankCode, country.code]);

  // Price re-fetches whenever crypto OR rateType changes (the price differs
  // between the two rate types). Network list only depends on crypto.
  // Splitting them avoids the bug where toggling fixed ⇄ float reset the
  // network selection back to the API's first entry.
  useEffect(() => {
    fetchPrice(crypto, rateType);
  }, [crypto, rateType, fetchPrice]);

  useEffect(() => {
    fetch(`/api/v1/networks?crypto=${crypto}`)
      .then((r) => r.json())
      .then((d) => {
        const nets: string[] = (d.networks ?? []).map((n: { network: string }) => n.network);
        setNetworks(nets);
        // Preserve the user's selection if it's still in the new list; only
        // fall back to the first option when crypto changed to one whose
        // network list doesn't include the prior choice (e.g. switching
        // from USDT, which has many networks, to BTC, which has one).
        setNetwork((current) => (current && nets.includes(current) ? current : nets[0] ?? ""));
      });
  }, [crypto]);

  // Auto-refresh the price every 30s while the user is on the page. Without
  // this a user who left the tab open for an hour submits at a stale rate
  // and gets a server-side rate_drift_exceeded 409 (also handled below) —
  // a refresh is much friendlier than an error. Cleared on unmount or when
  // the dependencies change so we never have two intervals running.
  useEffect(() => {
    const id = setInterval(() => {
      fetchPrice(crypto, rateType);
    }, 30_000);
    return () => clearInterval(id);
  }, [crypto, rateType, fetchPrice]);

  // Bidirectional amount: user can type either the crypto amount or the USD
  // equivalent, we compute the other on the fly. `amount` always holds the
  // text they see in the input (units = inputMode). `cryptoAmount` is what
  // we actually submit — always denominated in the crypto asset.
  const marketPrice = priceData?.marketPrice ?? 0;
  const parsedInput = parseFloat(amount);
  const validInput = !isNaN(parsedInput) && parsedInput > 0;

  // Predict the routing the server will pick — same logic as
  // resolveWalletRoute in lib/wallet/router.ts: forceOwnWallet pins to own,
  // otherwise compare market USD value to the per-crypto threshold. Drives
  // which spread the user actually pays, so the displayed estimate matches
  // what the trade will be created at.
  const marketUsdValue = validInput
    ? (inputMode === "usd" ? parsedInput : parsedInput * marketPrice)
    : 0;
  const ffioThreshold = priceData?.ffioThresholdUsd ?? TRADE_LIMITS.FFIO_THRESHOLD_USD;
  const effectiveRoute: "ffio" | "own_wallet" =
    !priceData
      ? "ffio"
      : (priceData.forceOwnWallet || marketUsdValue >= ffioThreshold ? "own_wallet" : "ffio");

  // Pick the matching userPrice from the (routing, rateType) matrix. Falls
  // back to the back-compat `userPrice` field if the new payload structure
  // isn't present (e.g. from a cached response during a rolling deploy).
  const userPrice = (() => {
    if (!priceData) return 0;
    if (priceData.userPrices) {
      const block = effectiveRoute === "own_wallet" ? priceData.userPrices.own : priceData.userPrices.ffio;
      return rateType === "float" ? block.float : block.fixed;
    }
    return priceData.userPrice;
  })();

  const cryptoAmount = validInput
    ? (inputMode === "crypto" ? parsedInput : (userPrice > 0 ? parsedInput / userPrice : 0))
    : 0;
  const estimatedUsd = validInput
    ? (inputMode === "usd" ? parsedInput : parsedInput * userPrice)
    : 0;

  // currencySymbol resolves from the rate's currency (the trade's actual
  // currency once rate loads) and falls back to the user-selected country's
  // symbol while loading — never the hardcoded "₦".
  const currencySymbol = rate ? currencySymbolFor(rate.currency) : country.currencySymbol;
  const estimatedLocal = rate && estimatedUsd > 0
    ? formatLocalAmount(estimatedUsd * rate.userRate, rate.currency)
    : null;

  function switchInputMode(next: "crypto" | "usd") {
    if (next === inputMode) return;
    // Preserve the current value by converting — no awkward reset on swap.
    // Skip the conversion (just flip mode) when userPrice hasn't loaded yet,
    // otherwise estimatedUsd / cryptoAmount evaluate to 0 and we'd silently
    // wipe the user's typed value to "0.00" / "0".
    if (validInput && userPrice > 0) {
      if (next === "usd") setAmount(estimatedUsd.toFixed(2));
      else setAmount(cryptoAmount.toFixed(8).replace(/0+$/, "").replace(/\.$/, ""));
    }
    setInputMode(next);
  }

  const selectedMethod = savedMethods.find((m) => m.id === selectedMethodId) ?? null;

  // Live promo-code check. Fires when the user clicks "Apply" next to
  // the code input on the review step. Re-validates server-side at
  // submit time inside initiateTrade — this preview is advisory.
  async function applyPromo() {
    const code = promoCodeInput.trim();
    if (!code) return;
    setPromoStatus({ state: "checking" });
    try {
      const res = await fetchWithCsrf("/api/v1/rewards/validate-promo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, usdValue: estimatedUsd }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setPromoStatus({ state: "rejected", message: data.message ?? "We couldn't apply that code." });
        return;
      }
      setPromoStatus({
        state: "applied",
        code: data.code,
        rewardAmount: data.rewardAmount,
        rewardKind: data.rewardKind,
        description: data.description,
      });
    } catch (err) {
      setPromoStatus({ state: "rejected", message: err instanceof Error ? err.message : "Failed to validate" });
    }
  }

  function clearPromo() {
    setPromoCodeInput("");
    setPromoStatus({ state: "idle" });
  }

  async function handleSubmit() {
    setError("");
    setErrorAction(null);

    // Anonymous-user recovery channel. Anon users have no account to
    // log back into, so if they close the tracking tab their only
    // path back to the trade is the receipt email. Prompt before
    // creating the trade so the email is available from minute zero
    // (used by notifyTradeCreated to send the tracking URL). Pure
    // opt-in — empty input or Cancel both proceed without the email.
    let receiptEmail: string | null = null;
    if (!isAuthenticated) {
      const entered = await promptDialog({
        title: "Track this trade by email?",
        message:
          "Optional. We'll email you the deposit address and a recovery link so you can return to this trade if you close this tab. We never spam.",
        placeholder: "you@example.com",
        confirmLabel: "Save & continue",
        cancelLabel: "Skip",
        required: false,
        maxLength: 255,
      });
      // Trim + light client-side format check. Server re-validates.
      // null = user clicked Skip → proceed without an email.
      const trimmed = (entered ?? "").trim();
      if (trimmed) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
          setError("That doesn't look like a valid email. Try again, or click Skip to continue without one.");
          return;
        }
        receiptEmail = trimmed;
      }
    }

    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        cryptoType: crypto,
        network,
        cryptoAmount, // always in crypto units, derived from the bidirectional input
        countryCode: country.code,
        source: "web",
        rateType,
        // Per-submit dedup key. Stamped on Transaction.idempotencyKey
        // server-side and used as the ff.io exchange dedup key. A double-
        // click on Confirm (between the time we set loading=true and the
        // server responds — usually a non-issue thanks to the disabled
        // button, but races still happen on flaky networks) collapses to
        // one trade because both requests carry the same UUID. Generated
        // fresh per click since each click is a distinct user intent.
        // `crypto` shadows the global in this scope (selected crypto
        // type), so we read the Web Crypto API off `window` explicitly.
        idempotencyKey:
          typeof window !== "undefined" && window.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        // Pin the user's expectation. Server compares to its freshly-fetched
        // live rate; if drift exceeds tolerance the response is a 409 with
        // code "rate_drift_exceeded" so we can refresh + ask the user to
        // re-confirm at the new rate (handled in the response branch below).
        ...(estimatedUsd > 0 ? { expectedUsdValue: estimatedUsd } : {}),
        // Server validates with z.string().email().optional(); empty
        // strings are tolerated for ergonomics. We only set the field
        // when the user actually provided one.
        ...(receiptEmail ? { receiptEmail } : {}),
        // Only attach the promo code when the live-validation
        // preview said yes. A code typed but never applied isn't sent
        // — server-side re-validation is the final word, and a
        // server-side rejection here would surface the same error
        // as the preview did, just later.
        ...(promoStatus.state === "applied" ? { promoCode: promoStatus.code } : {}),
      };

      if (selectedMethod) {
        body.savedMethodId = selectedMethod.id;
      } else {
        // Save-for-future: persist the account to the user's saved methods
        // BEFORE creating the trade, then reference it by id. We save first
        // so a POST failure surfaces here instead of after the trade is
        // already locked in — users would otherwise be confused by a
        // "saved a trade but forgot my bank account" outcome.
        // Silent-fail on 409 (duplicate) is intentional: if they already
        // have this account saved, just proceed with the trade inline.
        if (isAuthenticated && saveForFuture && accountName && accountNumber && bankCode) {
          try {
            const saveRes = await fetchWithCsrf("/api/v1/users/payment-methods", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                type: "bank_transfer",
                countryCode: country.code,
                accountName,
                accountNumber,
                bankCode,
                bankName: bankName || undefined,
              }),
            });
            if (saveRes.ok) {
              const saved = await saveRes.json();
              body.savedMethodId = saved.id;
            }
          } catch { /* non-fatal — fall through to inline paymentMethod */ }
        }

        if (!body.savedMethodId) {
          body.paymentMethod = {
            type: "bank_transfer",
            accountName,
            accountNumber,
            bankCode: bankCode || undefined,
            bankName: bankName || undefined,
          };
        }
      }

      // Bot-check signals — server enforces only for anonymous users
      // but we always include them so the server can decide.
      body[HONEYPOT_FIELD] = honeypot;
      body.pageLoadedAt = pageLoadedAtRef.current;

      // Age + ToS + Refund Policy acknowledgement. Always included
      // from the form state — the server skips this entirely when the
      // identity already has acceptance on file (registered users, or
      // anonymous sessions that ticked on a previous /sell). Sending
      // the bundle even for already-accepted users is harmless and
      // keeps the client simple. For first-time anonymous users the
      // submit button is disabled until they tick the checkbox.
      if (acknowledged) {
        body.acknowledgement = {
          acceptedAge: true,
          acceptedTos: true,
          acceptedRefundPolicy: true,
        };
      }

      const res = await fetchWithCsrf("/api/v1/transactions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        // Rate moved since the user saw the quote. Refresh in the background
        // so the displayed estimate updates immediately, then surface the
        // server's message so they can re-confirm at the new rate.
        if (data.code === "rate_drift_exceeded") {
          void fetchPrice(crypto, rateType);
        }
        // Daily-cap exceeded — populate the structured side-channel so
        // the review card can render a "Create account" / "Verify ID"
        // CTA instead of just a flat error pill. Anonymous users hitting
        // the $150 cap is the most common case and the CTA is by far
        // the most useful next step for them.
        if (data.code === "daily_limit_exceeded") {
          setErrorAction({
            kind: "daily_limit",
            tier: data.tier === "anonymous" ? "anonymous" : "verified",
            cap: Number(data.cap ?? 0),
            traded: Number(data.traded ?? 0),
            requested: Number(data.requested ?? 0),
          });
        } else {
          setErrorAction(null);
        }
        setError(data.error ?? "Failed to create trade");
        return;
      }
      setResult(data);
      setStep("done");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (step === "done" && result) {
    return <TradeCreated result={result} isAuthenticated={isAuthenticated} />;
  }

  // Admin accounts are blocked from trading server-side. Render a static
  // message instead of the wizard so they aren't lured through 4 steps just
  // to hit a 403 on submit.
  if (isAdmin) {
    return (
      <div className="flex justify-center">
        <div className="w-full max-w-md">
          <Card className="shadow-sm">
            <CardContent className="py-10 text-center space-y-3">
              <div className="inline-flex h-12 w-12 rounded-2xl bg-muted items-center justify-center">
                <Lock size={20} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold">Admin accounts can&apos;t trade</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                To keep admin and user responsibilities separate, this account isn&apos;t allowed to sell crypto. Sign in with a regular account to trade.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentStepIndex = WIZARD_STEPS.indexOf(step as Step);

  return (
    <div className="flex justify-center">
      {/* Outer dialog modal — used for the anonymous-user email prompt
          on submit. Mounted once at the wizard's root so the dialog
          renders above the form regardless of which step is active. */}
      {outerDialogModal}
      {/* Rate-type help modal — shows when the user taps the (?) on the
          Rate type label. Z-index above the form, click-outside dismisses.
          Copy mirrors the standard explanation users get on similar exchanges
          so the mental model carries over. */}
      {showRateHelp && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowRateHelp(false)}
        >
          <div
            className="relative w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl bg-background border border-border shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rate-help-title"
          >
            <div className="sticky top-0 bg-background border-b border-border px-5 py-3.5 flex items-center justify-between">
              <h2 id="rate-help-title" className="text-base font-bold">
                Fixed vs Float rates
              </h2>
              <button
                type="button"
                onClick={() => setShowRateHelp(false)}
                aria-label="Close"
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-5 text-sm leading-relaxed">
              {/* Fixed */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={16} className="text-primary" />
                  <h3 className="text-sm font-bold text-primary">Fixed rate</h3>
                </div>
                <p className="text-foreground">
                  Opting for a fixed rate, you get the price you see at the point of initiating a transaction.
                </p>
                <ul className="list-disc list-outside pl-5 mt-2 space-y-1.5 text-muted-foreground text-[13px]">
                  <li>Pay just <span className="font-semibold text-foreground">1% + network fee</span>.</li>
                  <li>Rates freeze for <span className="font-semibold text-foreground">10 minutes</span>.</li>
                  <li>
                    If the market rate changes by more than <span className="font-semibold text-foreground">1.2%</span> before
                    the appearance of your transaction on the blockchain network, you will be asked to make a refund or continue
                    exchanging at the market rate.
                  </li>
                </ul>
                <div className="mt-2.5 rounded-lg bg-warning-bg border border-warning-border px-3 py-2 flex items-start gap-2 text-[12px] text-warning-fg">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  <span>
                    <span className="font-semibold">Attention!</span> Your transaction must be received within 10 minutes and
                    the amount must exactly match the amount of the order. Otherwise, you will be prompted to make a refund or
                    continue the exchange at the market rate.
                  </span>
                </div>
              </section>

              {/* Float */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={16} className="text-warning-fg" />
                  <h3 className="text-sm font-bold text-warning-fg">Float rate</h3>
                </div>
                <p className="text-foreground">
                  The exchange rate is finally set when your transaction receives the necessary number of blockchain network
                  confirmations. If the market goes up, you will get more cryptocurrency, if down — less. All fair.
                </p>
                <ul className="list-disc list-outside pl-5 mt-2 space-y-1.5 text-muted-foreground text-[13px]">
                  <li>Pay just <span className="font-semibold text-foreground">0.5% + network fee</span>.</li>
                  <li>
                    The exchange will be made at the market rate, which is finally set within 10 minutes after your transaction
                    receives the required number of confirmations in the blockchain network.
                  </li>
                </ul>
              </section>

              {/* Example — Smokoin's product is crypto → local currency, so
                  the example uses the user's actual currency to make the
                  "what you get" promise concrete. ff.io's stock example
                  (crypto → crypto) doesn't apply here. */}
              {(() => {
                // Falls back to the currently-selected country's currency
                // (not "NGN"!) when rate hasn't loaded — keeps the example
                // consistent with what the user actually sees in the form.
                const currencyCode = rate?.currency ?? country.currency;
                const sym = currencySymbol;
                // Round-number examples per currency. Add new entries as
                // we onboard countries; the lookup degrades to NGN-style
                // example for anything we haven't tuned yet.
                const exampleByCurrency: Record<string, string> = {
                  NGN: "1,500,000",
                  GHS: "5,000",
                };
                const exampleLocal = exampleByCurrency[currencyCode] ?? "1,500,000";
                return (
                  <section className="rounded-lg bg-muted px-3 py-2.5 text-[12px] text-muted-foreground">
                    <span className="font-semibold text-foreground">Example: </span>
                    you sell crypto for{" "}
                    <span className="font-semibold text-foreground">
                      {sym}{exampleLocal}
                    </span>{" "}
                    at the quoted rate. With a <span className="font-semibold text-foreground">fixed</span> rate, you
                    receive exactly {sym}{exampleLocal} — even if the market moves while your deposit is confirming.
                    With a <span className="font-semibold text-foreground">float</span> rate, the {currencyCode} amount
                    can go up or down depending on the live market price when your deposit confirms.
                  </section>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-md space-y-5">

        {/* Pending-trade banner — recovery surface for users who already
            have an in-flight trade attached to their session. Renders
            above the wizard so they can resume in one tap instead of
            starting a new trade by mistake. */}
        <PendingTradeBanner />

        {/* Step indicator — wider labels, cleaner connector */}
        <div className="flex items-center gap-0">
          {WIZARD_STEPS.map((s, i) => {
            const isDone = currentStepIndex > i;
            const isActive = step === s;
            return (
              <div key={s} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center gap-1">
                  <span className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all duration-200",
                    isActive ? "bg-primary text-primary-foreground shadow-sm ring-4 ring-primary/15" :
                    isDone ? "bg-green-500 text-white" :
                    "bg-muted text-muted-foreground"
                  )}>
                    {isDone ? <Check size={12} /> : i + 1}
                  </span>
                  <span className={cn(
                    "text-[10px] font-medium tracking-wide",
                    isActive ? "text-primary" : isDone ? "text-success-fg" : "text-muted-foreground"
                  )}>
                    {STEP_LABELS[s]}
                  </span>
                </div>
                {i < WIZARD_STEPS.length - 1 && (
                  <div className={cn(
                    "flex-1 h-px mx-2 mb-4 transition-colors",
                    isDone ? "bg-green-400" : "bg-border"
                  )} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step 1: Crypto + Network */}
        {step === "crypto" && (
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">What are you selling?</CardTitle>
              <p className="text-sm text-muted-foreground">Choose a coin and the network to send on.</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Coin grid */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Coin</Label>
                <div className="grid grid-cols-4 gap-2">
                  {CRYPTOS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCrypto(c)}
                      className={cn(
                        "rounded-xl border-2 p-3 text-center transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        crypto === c
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border hover:border-primary/40 hover:bg-muted/30"
                      )}
                    >
                      <div className="flex justify-center mb-1.5">
                        <CryptoIcon symbol={c} size={28} />
                      </div>
                      <div className={cn(
                        "text-xs font-semibold",
                        crypto === c ? "text-primary" : "text-foreground"
                      )}>{c}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Network list */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Network</Label>
                <div className="space-y-2">
                  {networks.length === 0 ? (
                    <div className="h-16 rounded-xl border border-border bg-muted/30 animate-pulse" />
                  ) : networks.map((n) => (
                    <button
                      key={n}
                      onClick={() => setNetwork(n)}
                      className={cn(
                        "w-full rounded-xl border-2 p-3.5 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        network === n
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border hover:border-primary/40 hover:bg-muted/20"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm font-semibold", network === n ? "text-primary" : "text-foreground")}>
                          {NETWORK_LABELS[n] ?? n}
                        </span>
                        {network === n && <Check size={15} className="text-primary shrink-0" />}
                      </div>
                      {NETWORK_WARNINGS[n] && (
                        <div className="flex items-start gap-1.5 mt-1.5">
                          <AlertTriangle size={11} className="text-amber-500 shrink-0 mt-0.5" />
                          <span className="text-xs text-warning-fg leading-relaxed">{NETWORK_WARNINGS[n]}</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                className="w-full h-11 text-sm font-semibold"
                disabled={!network}
                onClick={() => setStep("amount")}
              >
                Continue <ChevronRight size={15} className="ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Amount + Rate type */}
        {step === "amount" && (
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2.5">
                <CryptoIcon symbol={crypto} size={28} />
                <CardTitle className="text-lg">How much {crypto} are you selling?</CardTitle>
              </div>
              <p className="text-sm text-muted-foreground">Enter the amount you want to convert.</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Live price ticker — the per-unit price shown reflects the
                  EFFECTIVE spread for this trade: routing × rate type. The
                  routing pill (own / ff.io) and rate-type pill change as the
                  user types and toggles, so the price updates visibly when
                  any of those inputs cross a threshold. */}
              <div className="rounded-xl border border-border bg-muted/30 p-3.5">
                {priceLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 size={12} className="animate-spin" />
                    Fetching live price…
                  </div>
                ) : priceData ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground inline-flex items-center gap-1.5 flex-wrap">
                        Your {crypto} rate
                        <span className={cn(
                          "text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full",
                          rateType === "fixed"
                            ? "bg-primary/10 text-primary"
                            : "bg-warning-bg text-warning-fg"
                        )}>
                          {rateType}
                        </span>
                      </span>
                      <span className="text-sm font-bold tabular-nums">
                        ${userPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {/* Show the live market price + effective spread beneath
                        so users can see what they're paying. USDT skips
                        (market === userPrice always). */}
                    {priceData.marketPrice > 0 && priceData.marketPrice !== userPrice && (
                      <div className="flex items-center justify-between -mt-1">
                        <span className="text-[11px] text-muted-foreground">
                          Market ${priceData.marketPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="text-[11px] text-muted-foreground tabular-nums">
                          spread {((1 - userPrice / priceData.marketPrice) * 100).toFixed(2)}%
                        </span>
                      </div>
                    )}
                    {/* Freshness hint — auto-refresh runs every 30s but
                        showing the timestamp lets a vigilant user verify
                        the rate isn't from minutes ago (e.g. if they
                        backgrounded the tab and came back). */}
                    {priceFetchedAt && (
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        Updated {Math.max(0, Math.round((Date.now() - priceFetchedAt) / 1000))}s ago
                      </div>
                    )}
                    {rate && (
                      <div className="flex items-center justify-between border-t border-border/60 pt-2">
                        <span className="text-xs text-muted-foreground">Payout rate</span>
                        <span className="text-xs font-semibold tabular-nums">
                          {currencySymbol}{formatLocalAmount(rate.userRate, rate.currency)}<span className="font-normal text-muted-foreground">/USD</span>
                        </span>
                      </div>
                    )}
                    {rateError && (
                      <div className="border-t border-border/60 pt-2">
                        <span className="text-xs text-warning-fg">Payout rate calculated at confirmation</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-destructive">Unable to fetch live price — try refreshing</p>
                )}
              </div>

              {/* Amount input — bidirectional (crypto ↔ USD) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="amount" className="text-sm font-medium">
                    Amount
                  </Label>
                  {/* Unit toggle */}
                  <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40">
                    <button
                      type="button"
                      onClick={() => switchInputMode("crypto")}
                      className={cn(
                        "px-2.5 py-1 rounded-md text-xs font-semibold transition-colors",
                        inputMode === "crypto"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {crypto}
                    </button>
                    <button
                      type="button"
                      onClick={() => switchInputMode("usd")}
                      className={cn(
                        "px-2.5 py-1 rounded-md text-xs font-semibold transition-colors",
                        inputMode === "usd"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      USD
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <Input
                    id="amount"
                    type="number"
                    step="any"
                    min="0"
                    placeholder={
                      inputMode === "usd"
                        ? "e.g. 100"
                        : `e.g. ${crypto === "BTC" ? "0.005" : crypto === "ETH" ? "0.1" : "10"}`
                    }
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="h-14 text-lg font-mono pr-20"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground pointer-events-none">
                    {inputMode === "usd" ? "USD" : crypto}
                  </span>
                </div>

                {/* Converted value — click to flip input mode */}
                {validInput && userPrice > 0 && (
                  <button
                    type="button"
                    onClick={() => switchInputMode(inputMode === "crypto" ? "usd" : "crypto")}
                    className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                    title="Tap to edit this amount instead"
                  >
                    <ArrowLeftRight size={11} />
                    <span className="font-mono tabular-nums">
                      {inputMode === "crypto"
                        ? `≈ $${estimatedUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : `≈ ${cryptoAmount.toFixed(8).replace(/\.?0+$/, "")} ${crypto}`}
                    </span>
                  </button>
                )}

                {/* Per-asset minimum trade hint. Reads from the price
                    payload (admin-configurable at /admin/limits → Wallet).
                    Renders red when the current amount is below the floor
                    so the user can adjust before submitting and getting a
                    409 — server still enforces. Hidden when min = 0. */}
                {(priceData?.minTradeUsd ?? 0) > 0 && (
                  <p className={cn(
                    "text-[11px] text-center tabular-nums",
                    estimatedUsd > 0 && estimatedUsd < (priceData?.minTradeUsd ?? 0)
                      ? "text-danger-fg font-medium"
                      : "text-muted-foreground"
                  )}>
                    Minimum trade: ${(priceData?.minTradeUsd ?? 0).toFixed(2)}
                    {estimatedUsd > 0 && estimatedUsd < (priceData?.minTradeUsd ?? 0) && (
                      <span> — your amount is below this</span>
                    )}
                  </p>
                )}

                {/* Live payout panel + country toggle.
                    The toggle lets the user flip between active currencies
                    inline (no separate wizard step). Renders dynamically
                    from the active-countries list — adding a 3rd country
                    just appears here automatically. Only shown if there's
                    more than one option. */}
                {estimatedUsd > 0 && (
                  <div className="rounded-xl border border-border bg-background overflow-hidden">
                    {estimatedLocal ? (
                      <div className="flex justify-between items-center px-3.5 py-3 bg-primary/5 gap-2 flex-wrap">
                        <span className="text-xs font-medium text-primary">You receive</span>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-primary tabular-nums">{currencySymbol}{estimatedLocal}</span>
                          {(activeCountries ?? allRegisteredCountries()).length > 1 && (
                            <div className="inline-flex rounded-md border border-border p-0.5 bg-background">
                              {(activeCountries ?? allRegisteredCountries()).map((c) => (
                                <button
                                  key={c.code}
                                  type="button"
                                  onClick={() => setCountry(c.code)}
                                  title={`Switch to ${c.name} (${c.currency})`}
                                  className={cn(
                                    "px-2 py-0.5 rounded text-[11px] font-bold transition-colors",
                                    country.code === c.code
                                      ? "bg-foreground text-background"
                                      : "text-muted-foreground hover:text-foreground"
                                  )}
                                >
                                  {c.flagEmoji} {c.currency}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : rateError ? (
                      <div className="px-3.5 py-2 text-xs text-warning-fg bg-warning-bg">
                        Final payout rate determined at confirmation
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Rate type selector */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium">Rate type</Label>
                  <button
                    type="button"
                    onClick={() => setShowRateHelp(true)}
                    aria-label="What's the difference between fixed and float?"
                    className="inline-flex items-center justify-center h-5 w-5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <HelpCircle size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setRateType("fixed")}
                    className={cn(
                      "rounded-xl border-2 p-3.5 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      rateType === "fixed"
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border hover:border-primary/40"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Lock size={14} className={rateType === "fixed" ? "text-primary" : "text-muted-foreground"} />
                      <span className={cn("text-sm font-semibold", rateType === "fixed" ? "text-primary" : "")}>Fixed</span>
                      {rateType === "fixed" && (
                        <span className="ml-auto text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium">Default</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Rate locked now. You get exactly what you see.
                    </p>
                  </button>
                  <button
                    onClick={() => setRateType("float")}
                    className={cn(
                      "rounded-xl border-2 p-3.5 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      rateType === "float"
                        ? "border-amber-500 bg-warning-bg shadow-sm"
                        : "border-border hover:border-warning-border"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <TrendingUp size={14} className={rateType === "float" ? "text-warning-fg" : "text-muted-foreground"} />
                      <span className={cn("text-sm font-semibold", rateType === "float" ? "text-warning-fg" : "")}>Float</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Rate adjusts at completion — could go up or down.
                    </p>
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="h-11" onClick={() => setStep("crypto")}>Back</Button>
                <Button
                  className="flex-1 h-11 font-semibold"
                  disabled={!validInput || cryptoAmount <= 0}
                  onClick={() => setStep("payment")}
                >
                  Continue <ChevronRight size={15} className="ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Payment */}
        {step === "payment" && (
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Where should we send your cash?</CardTitle>
              <p className="text-sm text-muted-foreground">Your payout will be sent to this account.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {isAuthenticated && savedMethods.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Saved accounts</Label>
                  <div className="space-y-2">
                    {savedMethods.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setSelectedMethodId(m.id)}
                        className={cn(
                          "w-full rounded-xl border-2 p-3.5 text-left transition-all duration-150",
                          selectedMethodId === m.id
                            ? "border-primary bg-primary/5 shadow-sm"
                            : "border-border hover:border-primary/30"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            {/* Lead with the user's nickname when set —
                                "Main savings" is more recognizable than
                                a stripped account name pulled from the
                                bank. Falls back to account name when no
                                nickname has been added. */}
                            <div className="text-sm font-semibold">{m.label ?? m.accountName}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {m.label && <>{m.accountName} · </>}
                              {m.maskedAccountNumber}
                              {(m.bankName || m.bankCode) && ` · ${m.bankName ?? m.bankCode}`}
                            </div>
                          </div>
                          {selectedMethodId === m.id && <Check size={15} className="text-primary shrink-0" />}
                        </div>
                      </button>
                    ))}
                    <button
                      onClick={() => setSelectedMethodId(null)}
                      className={cn(
                        "w-full rounded-xl border-2 border-dashed p-3.5 text-left transition-all duration-150",
                        selectedMethodId === null
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/30"
                      )}
                    >
                      <div className="text-sm font-medium text-muted-foreground">+ Use a different account</div>
                    </button>
                  </div>
                </div>
              )}

              {(!isAuthenticated || selectedMethodId === null) && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="accountNumber" className="text-sm font-medium">Account number</Label>
                    <Input
                      id="accountNumber"
                      placeholder="10-digit NUBAN"
                      maxLength={10}
                      value={accountNumber}
                      onChange={(e) => {
                        setAccountNumber(e.target.value.replace(/\D/g, ""));
                        setResolvedAccountName("");
                        setResolveError("");
                        setAccountName("");
                      }}
                      className="h-11 font-mono tracking-widest"
                    />
                  </div>

                  {/* Bank selector */}
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Bank</Label>
                    <div
                      ref={bankTriggerRef}
                      className={cn(
                        "flex items-center justify-between border-2 rounded-xl px-3.5 py-2.5 text-sm cursor-pointer bg-background hover:bg-muted/30 transition-colors h-11",
                        showBankList ? "border-primary" : "border-border"
                      )}
                      onClick={() => {
                        if (showBankList) {
                          setShowBankList(false);
                        } else {
                          setBankDropdownRect(bankTriggerRef.current?.getBoundingClientRect() ?? null);
                          setShowBankList(true);
                        }
                      }}
                    >
                      <span className={bankName ? "text-foreground font-medium" : "text-muted-foreground"}>
                        {bankName || "Select your bank"}
                      </span>
                      <span className="text-muted-foreground text-xs">{showBankList ? "▲" : "▼"}</span>
                    </div>
                  </div>

                  {/* Account name — auto-filled after resolution */}
                  <div className="space-y-1.5">
                    <Label htmlFor="accountName" className="text-sm font-medium">Account name</Label>
                    {resolving ? (
                      <div className="flex items-center gap-2.5 h-11 px-3.5 rounded-xl border-2 border-border bg-muted text-sm text-muted-foreground">
                        <Loader2 size={14} className="text-primary animate-spin shrink-0" />
                        Verifying account…
                      </div>
                    ) : resolvedAccountName ? (
                      <div className="flex items-center gap-2.5 h-11 px-3.5 rounded-xl border-2 border-green-400 bg-success-bg text-sm font-semibold text-success-fg">
                        <Check size={15} className="text-success-fg shrink-0" />
                        {resolvedAccountName}
                      </div>
                    ) : resolveError ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 h-11 px-3.5 rounded-xl border-2 border-danger-border bg-danger-bg text-sm text-danger-fg">
                          <AlertTriangle size={13} className="shrink-0" />
                          Account not found — check number &amp; bank
                        </div>
                        <Input
                          id="accountName"
                          placeholder="Enter account name manually"
                          value={accountName}
                          onChange={(e) => setAccountName(e.target.value)}
                          className="h-11"
                        />
                      </div>
                    ) : (
                      <Input
                        id="accountName"
                        placeholder={validateAccountNumber(country.code, accountNumber) && !bankCode
                          ? "Select a bank to verify"
                          : `Enter ${country.accountNumberLength}-digit account number first`}
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        disabled={!validateAccountNumber(country.code, accountNumber) || !bankCode}
                        className="h-11"
                      />
                    )}
                  </div>

                  {/* Bank dropdown rendered fixed */}
                  {showBankList && bankDropdownRect && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowBankList(false)} />
                      <div
                        className="fixed z-50 border border-border rounded-xl bg-background shadow-2xl overflow-hidden"
                        style={{
                          top: bankDropdownRect.bottom + 6,
                          left: bankDropdownRect.left,
                          width: bankDropdownRect.width,
                        }}
                      >
                        <div className="p-2 border-b border-border">
                          <Input
                            placeholder="Search banks…"
                            value={bankSearch}
                            onChange={(e) => setBankSearch(e.target.value)}
                            autoFocus
                            className="text-sm h-8"
                          />
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {banks
                            .filter((b) => b.name.toLowerCase().includes(bankSearch.toLowerCase()))
                            .map((b) => (
                              <button
                                key={b.code}
                                type="button"
                                className={cn(
                                  "w-full text-left px-3.5 py-2.5 text-sm hover:bg-muted transition-colors",
                                  bankCode === b.code && "bg-primary/10 font-semibold"
                                )}
                                onClick={() => {
                                  setBankCode(b.code);
                                  setBankName(b.name);
                                  setShowBankList(false);
                                  setBankSearch("");
                                  setResolvedAccountName("");
                                  setResolveError("");
                                  setAccountName("");
                                }}
                              >
                                {b.name}
                                <span className="ml-2 text-xs text-muted-foreground font-mono">{b.code}</span>
                              </button>
                            ))}
                          {banks.filter((b) => b.name.toLowerCase().includes(bankSearch.toLowerCase())).length === 0 && (
                            <p className="text-xs text-muted-foreground text-center py-5">No banks found</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Save-for-future toggle. Only shown to logged-in users —
                      anonymous sessions have no account to attach the saved
                      method to. The POST dedupes on (userId, accountNumber,
                      bank) so re-submitting a saved account is a no-op. */}
                  {isAuthenticated && (
                    <label className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-3.5 cursor-pointer hover:bg-muted/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={saveForFuture}
                        onChange={(e) => setSaveForFuture(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-border accent-primary shrink-0"
                      />
                      <div className="text-sm">
                        <div className="font-medium">Save this account for next time</div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          We&apos;ll pre-select it on your next trade. You can remove it anytime from Saved accounts.
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="h-11" onClick={() => setStep("amount")}>Back</Button>
                <Button
                  className="flex-1 h-11 font-semibold"
                  disabled={selectedMethodId ? false : (!accountName || !accountNumber || resolving)}
                  onClick={() => setStep("confirm")}
                >
                  Review trade <ChevronRight size={15} className="ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Confirm */}
        {step === "confirm" && (
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Review your trade</CardTitle>
              <p className="text-sm text-muted-foreground">Confirm the details before we generate your deposit address.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && !errorAction && (
                <Alert className="text-sm text-destructive border-destructive/30 bg-destructive/5">
                  <AlertTriangle size={14} className="mr-2 inline-block" />
                  {error}
                </Alert>
              )}

              {/* Age + ToS + Refund Policy acknowledgement. Placed at
                  the TOP of the confirm step so when the server bounces
                  with "acknowledgement_required" the user's eye lands
                  on the checkbox right next to the error banner — not
                  buried under the trade summary. Visibility is driven
                  by /auth/me's acknowledgedCurrent flag, NOT just
                  authentication state — so registered users who
                  predate the gate, or anyone whose ToS effective date
                  has moved past their stored acceptance, also see
                  this card and re-tick. The card uses warning tint
                  when an acknowledgement error has fired so the user
                  sees a consistent "this is what you need to fix"
                  treatment next to the error pill. */}
              {!acknowledgedCurrent && (
                <label
                  className={cn(
                    "flex items-start gap-3 text-sm cursor-pointer select-none border-2 rounded-xl px-4 py-3.5 transition-colors",
                    acknowledged
                      ? "border-success-border bg-success-bg/40"
                      : error
                        ? "border-warning-border bg-warning-bg/50"
                        : "border-border bg-muted/20 hover:bg-muted/30",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="mt-0.5 h-5 w-5 rounded border-border accent-primary shrink-0 cursor-pointer"
                  />
                  <span className="leading-snug font-medium">
                    I confirm I am at least <strong>18 years old</strong> and accept our{" "}
                    <Link href="/legal/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link href="/legal/refunds" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                      Refund Policy
                    </Link>
                    .
                  </span>
                </label>
              )}
              {errorAction?.kind === "daily_limit" && (
                <div className="rounded-xl border-2 border-warning-border bg-warning-bg/50 p-4 space-y-3">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle size={16} className="mt-0.5 text-warning-fg shrink-0" />
                    <div className="text-sm font-semibold text-warning-fg">
                      {errorAction.tier === "anonymous"
                        ? `Anonymous trades are capped at $${errorAction.cap.toFixed(0)}/day`
                        : "Daily trade limit reached"}
                    </div>
                  </div>
                  <div className="text-sm text-foreground/90 space-y-2 pl-[26px]">
                    <p>
                      Today's usage:{" "}
                      <span className="font-semibold tabular-nums">
                        ${errorAction.traded.toFixed(2)}
                      </span>{" "}
                      of{" "}
                      <span className="font-semibold tabular-nums">
                        ${errorAction.cap.toFixed(0)}
                      </span>
                    </p>
                    <p>
                      This trade is{" "}
                      <span className="font-semibold tabular-nums">
                        ${errorAction.requested.toFixed(2)}
                      </span>{" "}
                      — exceeds remaining{" "}
                      <span className="tabular-nums">
                        ${Math.max(0, errorAction.cap - errorAction.traded).toFixed(2)}
                      </span>
                      .
                    </p>
                  </div>
                  {errorAction.tier === "anonymous" ? (
                    <div className="pl-[26px] space-y-2">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Create a free account to unlock larger limits, save your bank details for one-tap trades, and access full transaction history.
                      </p>
                      <div className="flex gap-2">
                        <Link
                          href="/auth/register"
                          className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-semibold hover:opacity-90"
                        >
                          Create account
                        </Link>
                        <Link
                          href="/auth/login"
                          className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-1.5 text-sm font-semibold hover:bg-muted"
                        >
                          Sign in
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="pl-[26px] space-y-2">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Try again tomorrow, reduce the amount to fit the remaining limit, or verify your ID for a higher tier.
                      </p>
                      <Link
                        href="/dashboard/verify"
                        className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-semibold hover:opacity-90"
                      >
                        Verify ID
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Summary card */}
              <div className="rounded-xl border border-border bg-muted/20 overflow-hidden divide-y divide-border">
                {([
                  ["You sell", `${cryptoAmount.toFixed(8).replace(/\.?0+$/, "")} ${crypto}`],
                  ["Network", NETWORK_LABELS[network] ?? network],
                  estimatedUsd > 0 ? ["USD value", `$${estimatedUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`] : null,
                  estimatedLocal ? ["You receive", `${currencySymbol}${estimatedLocal}`] : null,
                  selectedMethod ? ["Account name", selectedMethod.accountName] : ["Account name", accountName],
                  selectedMethod
                    ? ["Account no.", selectedMethod.maskedAccountNumber]
                    : ["Account no.", `${"•".repeat(Math.max(0, accountNumber.length - 4))}${accountNumber.slice(-4)}`],
                  (selectedMethod?.bankCode || (!selectedMethod && bankCode))
                    ? ["Bank", selectedMethod?.bankName || bankName || selectedMethod?.bankCode || bankCode]
                    : null,
                ] as (string[] | null)[]).filter((r): r is string[] => r !== null).map(([label, value]) => {
                  const isPayoutRow = label === "You receive";
                  return (
                    <div
                      key={label}
                      className={cn(
                        "flex justify-between items-center px-4 py-3 text-sm",
                        isPayoutRow && "bg-primary/5"
                      )}
                    >
                      <span className={cn("text-muted-foreground", isPayoutRow && "font-medium text-primary")}>{label}</span>
                      <span className={cn("font-medium", isPayoutRow && "font-bold text-primary text-base tabular-nums")}>{value}</span>
                    </div>
                  );
                })}
              </div>

              {rateType === "float" && (
                <div className="flex items-start gap-2.5 rounded-xl bg-warning-bg border border-warning-border px-3.5 py-3 text-xs text-warning-fg">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5 text-warning-fg" />
                  <span>Float rate selected — your payout will reflect the market rate when your deposit confirms. It may be higher or lower than the estimate above.</span>
                </div>
              )}

              {/* Promo code — optional row, only renders the input until the
                  user expands it. Validates live against the current trade's
                  USD value; the applied/rejected state shows below. */}
              <PromoCodeRow
                input={promoCodeInput}
                onInput={setPromoCodeInput}
                status={promoStatus}
                onApply={applyPromo}
                onClear={clearPromo}
              />

              {/* FixedFloat affiliate disclosure — required verbatim text per
                  ff.io's affiliate program rules: the notice must use their
                  exact wording, sit in a conspicuous place, and be easy to
                  read. Only shown when the trade is predicted to route via
                  ff.io; own-wallet trades skip this entirely.
                  DO NOT paraphrase the text — it's a compliance requirement. */}
              {effectiveRoute === "ffio" && (
                <div className="flex items-start gap-3 rounded-xl bg-background border-2 border-border px-4 py-3.5 text-sm text-foreground shadow-sm">
                  <Info size={16} className="shrink-0 mt-0.5 text-primary" />
                  <p className="leading-relaxed">
                    The exchange service is provided by{" "}
                    <a
                      href="https://ff.io/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline underline-offset-2 hover:opacity-80"
                    >
                      FixedFloat
                    </a>
                    . Creating an order confirms your agreement with the{" "}
                    <a
                      href="https://ff.io/rules"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline underline-offset-2 hover:opacity-80"
                    >
                      FixedFloat rules
                    </a>
                    .
                  </p>
                </div>
              )}

              {/* Honeypot field — hidden from real users, autofocus
                  is OFF, autocomplete is OFF, tabindex skips it. Bots
                  that auto-fill every input will populate it; the
                  server rejects on non-empty value.

                  We use `aria-hidden` + visually-hidden styles instead
                  of `display:none` because some bots filter out
                  display-none fields. Off-screen + zero size is the
                  best-known dodge.

                  Field name `nickname` (HONEYPOT_FIELD) is generic
                  enough that naive form-fillers treat it as a real
                  input. */}
              {!isAuthenticated && (
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    top: "-9999px",
                    width: 0,
                    height: 0,
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor={HONEYPOT_FIELD}>Nickname (leave empty)</label>
                  <input
                    id={HONEYPOT_FIELD}
                    name={HONEYPOT_FIELD}
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="h-11" onClick={() => setStep("payment")}>Back</Button>
                <Button
                  className="flex-1 h-11 font-semibold"
                  onClick={handleSubmit}
                  disabled={loading || (!acknowledgedCurrent && !acknowledged)}
                >
                  {loading ? (
                    <><Loader2 size={15} className="animate-spin mr-2" /> Creating trade…</>
                  ) : (
                    <>Confirm &amp; get address <ArrowRight size={15} className="ml-1.5" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── Trade Created confirmation ───────────────────────────────────────────────

function TradeCreated({ result, isAuthenticated }: { result: TradeResult; isAuthenticated: boolean }) {
  // Registered users get the gated dashboard URL (proxy.ts will send them to
  // login on a fresh browser). Anon users stay on the public tracking URL
  // since they have no account to sign into.
  const trackingPath = isAuthenticated
    ? `/dashboard/trades/${result.transactionId}`
    : `/transactions/${result.transactionId}`;
  // Full URL for the share/copy/email affordances. Reads window.origin
  // at click-time so we don't hardcode the production domain (works in
  // dev / staging / prod without env coupling).
  function trackingUrl(): string {
    if (typeof window === "undefined") return trackingPath;
    return `${window.location.origin}${trackingPath}`;
  }
  const { confirm: confirmDialog, notify: notifyDialog, prompt: promptDialog, modal: dialogModal } = useDialog();
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailingLink, setEmailingLink] = useState(false);
  const [linkEmailed, setLinkEmailed] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [cancelling, setCancelling] = useState(false);
  // Refs so the pagehide handler sees the latest values without re-registering.
  const hasInteractedRef = useRef(false);
  const intentionalNavRef = useRef(false);
  const sym = currencySymbolFor(result.localCurrency);

  async function copy(val: string) {
    hasInteractedRef.current = true; // copying the address = likely going to deposit
    const ok = await copyToClipboard(val);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2500); }
  }

  // Copy the tracking URL — distinct from copying the address. Marking
  // hasInteracted so the pagehide auto-cancel doesn't fire when the
  // user grabs the link to send to themselves elsewhere.
  async function copyTrackingLink() {
    hasInteractedRef.current = true;
    const ok = await copyToClipboard(trackingUrl());
    if (ok) { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500); }
  }

  // Recovery email — for users who skipped the email prompt at confirm
  // time but want a fallback link in their inbox now that they're
  // looking at the tracking screen. Anonymous-only; registered users
  // already have email on file. Calls set_receipt_email which both
  // persists the email AND fires the order-created email when this is
  // the FIRST email we've learned for the trade.
  async function emailMeTheLink() {
    if (emailingLink) return;
    const entered = await promptDialog({
      title: "Email me a recovery link",
      message:
        "We'll send the deposit address and a link to this trade so you can return to it from any device.",
      placeholder: "you@example.com",
      confirmLabel: "Send it",
      cancelLabel: "Cancel",
      required: true,
      maxLength: 255,
    });
    const trimmed = (entered ?? "").trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      await notifyDialog({
        title: "Invalid email",
        message: "That doesn't look like a valid email address. Try again.",
        okLabel: "OK",
      });
      return;
    }
    setEmailingLink(true);
    hasInteractedRef.current = true;
    try {
      const res = await fetchWithCsrf(`/api/v1/transactions/${result.transactionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "set_receipt_email", email: trimmed }),
      });
      if (res.ok) {
        setLinkEmailed(true);
        // Hold the success state for the lifetime of the screen — the
        // user is about to navigate away. No hide-after-N-seconds.
      } else {
        const data = await res.json().catch(() => ({} as Record<string, unknown>));
        await notifyDialog({
          title: "Couldn't send",
          message: (data as { error?: string }).error ?? "Please try again in a moment.",
          okLabel: "OK",
        });
      }
    } catch {
      await notifyDialog({
        title: "Network error",
        message: "Couldn't send the email — check your connection and try again.",
        okLabel: "OK",
      });
    } finally {
      setEmailingLink(false);
    }
  }

  function goToTracking() {
    intentionalNavRef.current = true;
    window.location.href = trackingPath;
  }

  async function handleCancel() {
    if (cancelling) return;

    // First confirmation — the standard "are you sure" before any
    // network call. Cheap, blocks accidental clicks.
    const ok = await confirmDialog({
      title: "Cancel this trade?",
      message: "Only do this if you haven't sent any crypto yet. If you've already sent funds, finish the trade — we'll still process the deposit.",
      confirmLabel: "Cancel trade",
      cancelLabel: "Keep trade",
      variant: "destructive",
    });
    if (!ok) return;

    setCancelling(true);
    intentionalNavRef.current = true;

    // Inner helper so we can retry once with acknowledgeAge:true after
    // the user confirms the grace-window dialog. The server returns
    // 409 + code=cancel_confirm_required for trades younger than ~60s,
    // so we surface a second prompt and re-submit on acceptance.
    async function submitCancel(acknowledgeAge: boolean): Promise<void> {
      const res = await fetchWithCsrf(`/api/v1/transactions/${result.transactionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cancel", ...(acknowledgeAge ? { acknowledgeAge: true } : {}) }),
      });

      // 200 → cancelled. Already-cancelled / not-found also fall here as
      // "we tried, move on" — no point keeping the user on this screen.
      if (res.ok) return;

      const data = await res.json().catch(() => ({} as Record<string, unknown>));

      // Grace-window — show the confirmation dialog and re-submit
      // with acknowledgeAge if the user accepts. Important: this is
      // the ONLY place we set acknowledgeAge=true. Tab-close beacons
      // and other silent cancel paths intentionally NEVER set it, so
      // the server's grace gate keeps trades alive when we can't ask.
      if ((data as { code?: string }).code === "cancel_confirm_required") {
        const ackOk = await confirmDialog({
          title: "Are you sure?",
          message:
            (data as { message?: string }).message ??
            "If you've already sent crypto from your wallet, it may still be on its way. Cancel anyway?",
          confirmLabel: "Yes, cancel anyway",
          cancelLabel: "Wait — keep the trade",
          variant: "destructive",
        });
        if (!ackOk) return; // user backed out — keep the trade
        return submitCancel(true);
      }

      // Deposit-detected — hard block. We tell the user and DO NOT
      // navigate away from the trade screen (they need the tracking
      // page to see the deposit progress). `notify` is a single-
      // button informational dialog; the user can only acknowledge.
      if ((data as { code?: string }).code === "deposit_detected") {
        await notifyDialog({
          title: "Can't cancel — deposit detected",
          message:
            (data as { error?: string }).error ??
            "We've spotted an incoming deposit. The trade is continuing automatically.",
          okLabel: "Got it",
        });
        // Send them to tracking so they can watch it confirm.
        window.location.href = `/transactions/${result.transactionId}`;
        return;
      }

      // Anything else — just navigate to tracking; the user can see
      // the live state there.
      // (Don't surface raw API errors — the tracking page tells the
      // story better than a toast does.)
    }

    try {
      await submitCancel(false);
    } finally {
      // Default landing: back to /sell to start fresh. Overridden in
      // the deposit-detected branch above where we send them to the
      // tracking page instead.
      if (window.location.pathname.startsWith("/sell")) {
        window.location.href = "/sell";
      }
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(interval);
          goToTracking();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.transactionId]);

  // Best-effort auto-cancel on tab close / back navigation — only if the user
  // never interacted with the address (no copy) AND we didn't navigate them
  // ourselves to the tracking page. If they copied the address we assume
  // they're about to send crypto and keep the trade alive.
  useEffect(() => {
    function maybeCancel() {
      if (hasInteractedRef.current || intentionalNavRef.current) return;
      const payload = new Blob(
        [JSON.stringify({ action: "cancel" })],
        { type: "application/json" },
      );
      navigator.sendBeacon(`/api/v1/transactions/${result.transactionId}`, payload);
    }
    window.addEventListener("pagehide", maybeCancel);
    return () => window.removeEventListener("pagehide", maybeCancel);
  }, [result.transactionId]);

  // Countdown ring progress (0–1)
  const progress = countdown / 60;
  const circumference = 2 * Math.PI * 20;

  return (
    <div className="flex justify-center">
      {dialogModal}
      <div className="w-full max-w-md space-y-4">

        {/* Success header */}
        <div className="rounded-2xl border border-success-border bg-success-bg px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
              <Check size={18} strokeWidth={3} />
            </span>
            <div>
              <p className="text-base font-bold text-success-fg">Trade created!</p>
              <p className="text-sm text-success-fg mt-0.5">
                Send exactly <strong>{result.cryptoAmount} {result.cryptoType}</strong> to the address below to complete your trade.
              </p>
            </div>
          </div>
        </div>

        {/* Payout summary */}
        <div className="rounded-2xl border border-border bg-background px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <CryptoIcon symbol={result.cryptoType} network={result.network} size={40} />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground font-medium">You send</p>
              <p className="text-sm font-semibold mt-0.5 truncate">{result.cryptoAmount} {result.cryptoType}</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-muted-foreground font-medium">You receive</p>
            <p className="text-xl font-bold tracking-tight mt-0.5 tabular-nums">{sym}{formatLocalAmount(result.localAmount, result.localCurrency)}</p>
          </div>
        </div>

        {/* QR + address */}
        <div className="rounded-2xl border border-border bg-background p-5 space-y-4">
          <div className="flex items-center justify-center gap-2">
            <CryptoIcon symbol={result.cryptoType} size={18} />
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Send {result.cryptoType} on {result.networkLabel}</p>
          </div>

          <div className="flex justify-center">
            <div className="p-3 rounded-2xl border border-border shadow-sm bg-white">
              <QRCodeSVG value={result.depositAddress} size={192} bgColor="#ffffff" fgColor="#000000" level="M" />
            </div>
          </div>

          <div className="flex items-center gap-2 bg-muted rounded-xl px-3.5 py-3">
            <p className="font-mono text-xs break-all flex-1 leading-relaxed select-all text-foreground">{result.depositAddress}</p>
            <button
              onClick={() => copy(result.depositAddress)}
              className={cn(
                "flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-all duration-200 shrink-0",
                copied
                  ? "bg-success-bg text-success-fg"
                  : "bg-background border border-border hover:bg-muted shadow-sm"
              )}
            >
              {copied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>

          <div className="space-y-2 text-sm border-t border-border pt-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Reference</span>
              <span className="font-mono text-xs text-muted-foreground">{result.transactionId.slice(0, 14)}…</span>
            </div>
          </div>
        </div>

        {/* Save-link card — recovery surface for users (especially
            anonymous ones) who close this tab. Three affordances:
              1. Copy the tracking URL
              2. Email yourself a recovery link (anon only — registered
                 users got it at trade creation by default)
              3. A bold warning that says "this link is your only way
                 back to this trade"
            Registered users still see the copy-link button — handy if
            they want to share the trade with their support agent. */}
        <div className="rounded-2xl border-2 border-warning-border bg-warning-bg/40 p-4 space-y-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-warning-fg shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p className="text-sm font-bold text-warning-fg">Save your trade link</p>
              <p className="text-xs text-warning-fg/90 leading-relaxed">
                If you close this tab without saving, you&apos;ll need this URL to come back.
                {!isAuthenticated && " Anonymous trades aren't tied to an account — saving is on you."}
              </p>
            </div>
          </div>

          {/* Tracking URL row — full URL visible, single-tap copy */}
          <div className="flex items-center gap-2 bg-background/80 rounded-xl px-3 py-2.5 border border-warning-border/50">
            <p className="font-mono text-[11px] break-all flex-1 leading-snug select-all text-foreground">
              {trackingUrl()}
            </p>
            <button
              onClick={copyTrackingLink}
              className={cn(
                "flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-all duration-200 shrink-0",
                linkCopied
                  ? "bg-success-bg text-success-fg"
                  : "bg-foreground text-background hover:opacity-90 shadow-sm"
              )}
            >
              {linkCopied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
            </button>
          </div>

          {/* Email-me-the-link button — anonymous users only. Registered
              users already received an order email; offering the
              button to them too would just create duplicate sends. */}
          {!isAuthenticated && (
            linkEmailed ? (
              <div className="rounded-lg bg-success-bg/60 border border-success-border px-3 py-2 text-xs text-success-fg flex items-center gap-1.5">
                <Check size={12} />
                Recovery link sent. Check your inbox (and spam folder).
              </div>
            ) : (
              <button
                onClick={emailMeTheLink}
                disabled={emailingLink}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-warning-border bg-background hover:bg-muted text-sm font-semibold py-2.5 disabled:opacity-50 transition-colors"
              >
                {emailingLink
                  ? <><Loader2 size={13} className="animate-spin" /> Sending…</>
                  : <><Mail size={13} /> Email me this trade</>
                }
              </button>
            )
          )}
        </div>

        {/* Track + countdown */}
        <button
          onClick={goToTracking}
          className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          Track my trade <ArrowRight size={15} />
        </button>

        <button
          onClick={handleCancel}
          disabled={cancelling}
          className="w-full py-2.5 text-xs text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
        >
          {cancelling ? "Cancelling…" : "Cancel this trade"}
        </button>

        {/* Countdown with visual ring */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <svg width="20" height="20" viewBox="0 0 44 44" className="-rotate-90">
            <circle cx="22" cy="22" r="20" fill="none" stroke="currentColor" strokeWidth="3" className="text-muted/30" />
            <circle
              cx="22" cy="22" r="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - progress)}
              className="text-primary transition-all duration-1000"
              strokeLinecap="round"
            />
          </svg>
          Redirecting to tracking in {countdown}s
        </div>
      </div>
    </div>
  );
}

// Promo code input. Collapsed by default ("Add code?" link); expands
// to a text field + Apply button when clicked. Once applied, the row
// turns into a success pill showing the bonus + a Remove link. Errors
// render inline beneath the input so users can fix the code without
// scrolling.
type PromoStatus =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "applied"; code: string; rewardAmount: number; rewardKind: "fixed_usd" | "free_trade"; description: string }
  | { state: "rejected"; message: string };

function PromoCodeRow({
  input,
  onInput,
  status,
  onApply,
  onClear,
}: {
  input: string;
  onInput: (v: string) => void;
  status: PromoStatus;
  onApply: () => void;
  onClear: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (status.state === "applied") {
    const rewardLabel = status.rewardKind === "fixed_usd"
      ? `+$${status.rewardAmount.toFixed(2)} bonus`
      : "Free trade";
    return (
      <div className="rounded-xl border border-success-border bg-success-bg/40 px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-success-fg flex items-center gap-2">
            <span aria-hidden>🎁</span> {status.code} applied — {rewardLabel}
          </p>
          <p className="text-xs text-success-fg/80 mt-0.5 truncate">{status.description}</p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-success-fg/80 hover:text-success-fg underline shrink-0"
        >
          Remove
        </button>
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
      >
        Got a promo code?
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-muted/20 px-4 py-3 space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => onInput(e.target.value.toUpperCase())}
          placeholder="Enter code"
          className="flex-1 text-sm font-mono border border-border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 uppercase"
          maxLength={32}
        />
        <button
          type="button"
          onClick={onApply}
          disabled={status.state === "checking" || !input.trim()}
          className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-50 hover:opacity-90"
        >
          {status.state === "checking" ? "Checking…" : "Apply"}
        </button>
        <button
          type="button"
          onClick={() => { onClear(); setExpanded(false); }}
          className="text-xs text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      {status.state === "rejected" && (
        <p className="text-xs text-danger-fg flex items-center gap-1.5">
          <span aria-hidden>•</span> {status.message}
        </p>
      )}
    </div>
  );
}
